/**
 * Business Controls → Booking Rules, read the same way by the site and the server (Traci, 25 Oct 2026: the minimum
 * notice on /book was fixed at 10 days and the late booking window and fee were fixed in code, so saved changes never
 * reached checkout).
 *
 *   allow_short_notice_bookings        off: the standard notice (10 days) applies
 *   minimum_booking_days               on: clients may book down to this many days before the event (Trera, 25 Oct:
 *                                      the number is the short-notice limit; the standard 10 is what it is based off)
 *   late_booking_window_days           the late booking fee applies when the event is this many days away or fewer
 *   late_booking_fee_per_member_cents  that fee, per team member staffed (photo + video = two)
 *
 * Rows come straight from system_settings. Missing or invalid values fall back to the code defaults.
 */
import { resolveLateRule } from '../pricing/priceBook.js'

export const BOOKING_RULE_KEYS = ['minimum_booking_days', 'allow_short_notice_bookings', 'late_booking_window_days', 'late_booking_fee_per_member_cents']
/** The standard notice when short-notice bookings are off. */
export const DEFAULT_MINIMUM_BOOKING_DAYS = 10

/** { minimumDays, allowShortNotice, lateWindowDays, lateFeePerMemberCents } from system_settings rows. */
export function bookingRulesFromSettings(rows = []) {
  const value = key => (rows || []).find(r => r?.setting_key === key)?.setting_value
  const min = Number(value('minimum_booking_days'))
  const late = resolveLateRule(value('late_booking_window_days'), value('late_booking_fee_per_member_cents'))
  return {
    minimumDays: Number.isInteger(min) && min >= 0 && min <= 365 ? min : DEFAULT_MINIMUM_BOOKING_DAYS,
    allowShortNotice: String(value('allow_short_notice_bookings')) === 'true',
    lateWindowDays: late.window_days,
    lateFeePerMemberCents: late.fee_per_member_cents
  }
}

export const DEFAULT_BOOKING_RULES = bookingRulesFromSettings([])

/** The server counts days from the business's date (Sacramento), not the server clock's UTC date. */
export const BUSINESS_TIME_ZONE = 'America/Los_Angeles'

function dateParts(now, timeZone) {
  if (!timeZone) return [now.getFullYear(), now.getMonth() + 1, now.getDate()]
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(now).map(x => [x.type, x.value]))
  return [Number(p.year), Number(p.month), Number(p.day)]
}

/** Whole calendar days from today to a YYYY-MM-DD event date (tomorrow is 1). Today is the browser's date, or the
 * date in timeZone when given. */
export function calendarDaysUntil(eventDate, now = new Date(), timeZone) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(eventDate || ''))
  if (!m) return NaN
  const [y, mo, d] = dateParts(now, timeZone)
  return Math.round((Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - Date.UTC(y, mo - 1, d)) / 86400000)
}

/** The fewest days out a client may book: the Minimum Booking Notice when short-notice bookings are on, otherwise the
 * standard notice. Never today. */
export function earliestBookingDays(rules = DEFAULT_BOOKING_RULES) {
  return Math.max(1, rules.allowShortNotice ? rules.minimumDays : DEFAULT_MINIMUM_BOOKING_DAYS)
}

export function isLateBooking(daysOut, rules = DEFAULT_BOOKING_RULES) {
  return Number.isFinite(daysOut) && daysOut >= 0 && daysOut <= rules.lateWindowDays
}

/** "$450" for the fee per team member. */
export function lateFeePerMemberText(rules = DEFAULT_BOOKING_RULES) {
  return `$${(rules.lateFeePerMemberCents / 100).toLocaleString('en-US')}`
}
